import type { GoalActionRequest as GoalRequest, GoalRecord, GoalSnapshot, GoalWriteAction } from "../../supabase/functions/_shared/goalActionDomain";
import { canonicalGoalRecord, goalSnapshotsEqual, hasGoalWriteProof } from "../../supabase/functions/_shared/goalActionDomain";
import { newGoalId } from "@/lib/goals/progress";
import { claimConnectorWrite, finishConnectorWrite, loadConnectorWrite, type ConnectorWriteBody, type PendingConnectorWrite } from "@/lib/connectorPendingWrite";

export type { GoalActionRequest as GoalRequest, GoalRecord, GoalSnapshot, GoalWriteAction } from "../../supabase/functions/_shared/goalActionDomain";
export const GOAL_WRITE_ACTIONS = ["create", "update", "delete", "set_progress", "complete", "reopen", "add_milestone", "update_milestone", "delete_milestone", "set_milestone"] as const;
export type GoalAction = "search" | "get" | GoalWriteAction;
export interface GoalActionPrefill {
  action: GoalAction; action_id: string; operation_id: string;
  goal_id?: string; milestone_id?: string; query?: string;
  title?: string; description?: string | null; category?: string; due_date?: string | null;
  progress_override?: number | null; text?: string; is_done?: boolean;
  milestones?: Array<{ id: string; text: string }>;
  expected_user_id?: string; expected_session_id?: string;
  recovery_only?: boolean;
}
export interface GoalResponse extends Record<string, unknown> {
  ok: true; action?: string; operation_id?: string; goal_id?: string; goal?: GoalRecord;
  goals?: GoalRecord[]; complete?: boolean; has_more?: boolean;
  result?: Record<string, unknown>; receipt_id?: string;
}
export class GoalExecutorError extends Error {
  constructor(code: string, public context?: Response, public safe_retry = false, public confirmed_failed = false) { super(code); this.name = "GoalExecutorError"; }
}
export const goalUuid = (value: unknown): value is string => typeof value === "string" && /^[0-9a-f]{8}-[0-9a-f]{4}-[1-8][0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/i.test(value);
const object = (value: unknown): value is Record<string, unknown> => !!value && typeof value === "object" && !Array.isArray(value);
export const goalOperationKey = (operationId: string, action: string) => "mirror_goal_" + operationId + "_" + action + "_0";

/** The same captured turn resolves to the same operation after a modal remount. */
export async function goalOriginOperationId(ownerId: string, sessionId: string, actionId: string, kind = "operation") {
  const bytes = new Uint8Array(await crypto.subtle.digest("SHA-256", new TextEncoder().encode(JSON.stringify(["vyv-goal", ownerId, sessionId, actionId, kind])))).slice(0, 16);
  bytes[6] = (bytes[6] & 15) | 80; bytes[8] = (bytes[8] & 63) | 128;
  const hex = Array.from(bytes, byte => byte.toString(16).padStart(2, "0")).join("");
  return [hex.slice(0, 8), hex.slice(8, 12), hex.slice(12, 16), hex.slice(16, 20), hex.slice(20)].join("-");
}

export interface MirrorGoalReceiptReference {
  action: GoalWriteAction; operation_id: string; goal_id?: string;
  action_id: string; userId: string; sessionId: string;
}
export interface MirrorGoalOrigin { userId: string; sessionId: string; actionId: string }
export function matchesMirrorGoalReceiptOrigin(value: unknown, origin: MirrorGoalOrigin): value is MirrorGoalReceiptReference {
  return object(value) && goalUuid(origin.userId) && goalUuid(origin.sessionId) && /^[A-Za-z0-9._:-]{1,160}$/.test(origin.actionId) && value.userId === origin.userId &&
    value.sessionId === origin.sessionId && value.action_id === origin.actionId && goalUuid(value.operation_id) && value.operation_id !== origin.actionId &&
    GOAL_WRITE_ACTIONS.includes(value.action as GoalWriteAction) && (value.goal_id === undefined || goalUuid(value.goal_id));
}
export function mirrorGoalReceiptReference(entry: PendingConnectorWrite, origin: MirrorGoalOrigin): MirrorGoalReceiptReference | null {
  const body = entry.body;
  const value = { action: body.action, operation_id: body.operation_id, goal_id: body.goal_id,
    action_id: origin.actionId, userId: origin.userId, sessionId: origin.sessionId };
  if (entry.group !== "goal" || entry.key !== origin.userId + ":goal" || entry.ownerId !== origin.userId || entry.status !== "succeeded" ||
    !object(body.mirror_context) || body.mirror_context.session_id !== origin.sessionId || body.mirror_context.action_id !== origin.actionId ||
    body.idempotency_key !== goalOperationKey(String(body.operation_id), body.action) || !matchesMirrorGoalReceiptOrigin(value, origin)) return null;
  return value;
}

export function parseGoalRecord(value: unknown): GoalRecord | null {
  if (!object(value) || !Array.isArray(value.milestones) || value.milestones.length > 100 || typeof value.milestones_complete !== "boolean") return null;
  const fields = ["id", "user_id", "title", "description", "category", "due_date", "progress_override", "is_completed", "completed_at", "sort_order", "source", "external_id", "created_at", "updated_at"];
  try {
    const record = canonicalGoalRecord({ goal: Object.fromEntries(fields.map(field => [field, value[field]])), milestones: value.milestones, children_complete: value.milestones_complete });
    if (["progress", "writable", "read_only", "milestones_complete"].some(field => value[field] !== (record as unknown as Record<string, unknown>)[field]) ||
      typeof value.content_truncated !== "boolean" || value.milestones_complete && value.content_truncated ||
      record.snapshot && !goalSnapshotsEqual(value.snapshot, record.snapshot) || !record.snapshot && value.snapshot !== null) return null;
    return value as unknown as GoalRecord;
  } catch { return null; }
}

export function hasConfirmedGoalActionResult(body: Record<string, unknown>, value: unknown, ownerId?: string): value is GoalResponse {
  if (!object(value) || value.ok !== true || value.action !== body.action || value.operation_id !== body.operation_id ||
    !goalUuid(value.receipt_id) || value.goal_id !== body.goal_id || !object(value.result)) return false;
  return hasGoalWriteProof(body, value.result, ownerId);
}

/** A new preflight rejection does not establish the old attempt's outcome. */
export function isConfirmedGoalFailure(body: Record<string, unknown>, value: unknown): boolean {
  return object(value) && value.safe_retry === true && (value.receipt_status === "failed" || value.status === "failed") &&
    value.operation_id === body.operation_id && value.action === body.action && goalUuid(value.receipt_id);
}

export async function goalOwnerSession(ownerId: string) {
  const { supabase } = await import("@/integrations/supabase/client");
  const user = await supabase.auth.getUser();
  if (user.error || user.data.user?.id !== ownerId) throw new GoalExecutorError("invalid_session");
  const session = await supabase.auth.getSession();
  if (session.error || session.data.session?.user.id !== ownerId || !session.data.session.access_token) throw new GoalExecutorError("invalid_session");
  return session.data.session;
}
export async function invokeGoalAction(body: GoalRequest, ownerId: string): Promise<GoalResponse> {
  const session = await goalOwnerSession(ownerId);
  const { supabase } = await import("@/integrations/supabase/client");
  const response = await supabase.functions.invoke("goal-executor", { body, headers: { Authorization: "Bearer " + session.access_token } });
  await goalOwnerSession(ownerId);
  if (response.error) {
    const context = (response.error as { context?: Response }).context;
    if (context && typeof context.clone === "function") {
      try { const payload = await context.clone().json(); if (typeof payload?.error === "string") throw new GoalExecutorError(payload.error, context, payload.safe_retry === true, isConfirmedGoalFailure(body as unknown as Record<string, unknown>, payload)); }
      catch (cause) { if (cause instanceof GoalExecutorError) throw cause; }
    }
    throw new GoalExecutorError("outcome_unknown", context);
  }
  if (typeof response.data?.error === "string") throw new GoalExecutorError(response.data.error, undefined, response.data.safe_retry === true, isConfirmedGoalFailure(body as unknown as Record<string, unknown>, response.data));
  if (!object(response.data) || response.data.ok !== true || response.data.action !== body.action) throw new GoalExecutorError("outcome_unknown");
  if (GOAL_WRITE_ACTIONS.includes(body.action as GoalWriteAction) && !hasConfirmedGoalActionResult(body as unknown as Record<string, unknown>, response.data, ownerId)) throw new GoalExecutorError("outcome_unknown");
  return response.data as GoalResponse;
}

export const readPendingGoalAction = (ownerId: string) => loadConnectorWrite(ownerId, "goal");
/** Standalone module writes share the durable journal with reviewed captures. */
export async function executeReviewedGoalAction(ownerId: string, request: GoalRequest) {
  await goalOwnerSession(ownerId);
  const body = { ...request, idempotency_key: goalOperationKey(String(request.operation_id), request.action) } as ConnectorWriteBody;
  const claim = await claimConnectorWrite(ownerId, "goal", body, { title: typeof request.title === "string" ? request.title : undefined });
  if (JSON.stringify(claim.entry.body) !== JSON.stringify(body)) throw new GoalExecutorError("goal_pending_action");
  if (claim.entry.body.mirror_context) throw new GoalExecutorError("goal_pending_action");
  try {
    let response: GoalResponse;
    if (claim.entry.status === "succeeded") {
      const lookup = await invokeGoalAction({ action: "receipt", operation_id: String(body.operation_id) }, ownerId);
      response = { ...lookup, ok: true, action: body.action, operation_id: String(body.operation_id), goal_id: String(body.goal_id) };
      if (lookup.status !== "succeeded" || !hasConfirmedGoalActionResult(body, response, ownerId)) throw new GoalExecutorError("outcome_unknown");
    } else response = await invokeGoalAction(claim.entry.body as unknown as GoalRequest, ownerId);
    await finishConnectorWrite(claim.entry);
    const entry = { ...claim.entry, status: "succeeded" as const, confirmedResult: response };
    return { entry, response };
  } catch (cause) {
    if (claim.entry.status === "pending" && cause instanceof GoalExecutorError && cause.safe_retry && (claim.created || cause.confirmed_failed)) await finishConnectorWrite(claim.entry, true);
    throw cause;
  }
}
export async function acknowledgeGoalAction(entry: PendingConnectorWrite) {
  await goalOwnerSession(entry.ownerId);
  if (entry.group !== "goal" || entry.status !== "succeeded" || entry.body.mirror_context) throw new GoalExecutorError("goal_pending_action");
  await finishConnectorWrite(entry, true);
}
export const newGoalOperationId = newGoalId;
